import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { DepositModal, WithdrawModal, HistoryModal } from '@/features/transactions'

const GAMES = [
  {
    id: 'ruleta',
    nameKey: 'games.roulette',
    descKey: 'games.europeanRoulette',
    image: '/img/ruleta.jpg',
    color: 'from-red-900 to-red-950',
    available: true,
    requiresAuth: false,
  },
  {
    id: 'blackjack',
    nameKey: 'games.blackjack',
    descKey: 'games.classic21',
    image: '/img/blackjack.jpg',
    color: 'from-green-900 to-green-950',
    available: true,
    requiresAuth: false,
  },
  {
    id: 'baccarat',
    nameKey: 'games.baccarat',
    descKey: 'games.puntoBanco',
    image: '/img/baccarat.jpg',
    color: 'from-purple-900 to-purple-950',
    available: true,
    requiresAuth: false,
  },
  {
    id: 'blackjack-multiplayer',
    nameKey: 'games.blackjackMultiplayer',
    descKey: 'games.playWithOthers',
    image: '/img/blackjack-multi.jpg',
    color: 'from-blue-900 to-blue-950',
    available: true,
    requiresAuth: true, // Requiere registro para jugar
  },
  {
    id: 'slots',
    nameKey: 'games.slots',
    descKey: 'games.slotsDesc',
    image: '/img/slots.jpg',
    color: 'from-yellow-900 to-amber-950',
    available: true,
    requiresAuth: false,
  },
]

type ModalType = 'deposit' | 'withdraw' | 'history' | null

export default function Lobby() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, isDemo, logout } = useAuth()
  const [activeModal, setActiveModal] = useState<ModalType>(null)

  // Handler para ir a crear cuenta (sale del modo demo primero)
  const handleGoToRegister = useCallback(async () => {
    if (isDemo) {
      await logout()
    }
    navigate('/')
  }, [isDemo, logout, navigate])

  const handleGameClick = (gameId: string) => {
    navigate(`/game/${gameId}`)
  }

  if (!user) {
    navigate('/')
    return null
  }

  return (
    <>
      {/* Main content */}
      <div className="container mx-auto px-4 py-8">
        {/* Welcome message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h2 className="font-cinzel text-3xl md:text-4xl text-casino-gold mb-2">
            {t('lobby.welcome', { name: user.displayName?.split(' ')[0] || t('lobby.guest') })}
          </h2>
          <p className="text-gray-400">
            {isDemo ? t('lobby.playingDemo') : t('lobby.chooseFavorite')}
          </p>
        </motion.div>

        {/* Games grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {GAMES.map((game, index) => {
            // Determinar si el juego está bloqueado (requiere auth y usuario está en demo)
            const isLocked = game.requiresAuth && isDemo
            const isPlayable = game.available && !isLocked

            return (
              <motion.div
                key={game.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card
                  className={`group cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-gold overflow-hidden ${
                    !isPlayable && 'opacity-60 cursor-not-allowed hover:scale-100'
                  }`}
                  onClick={() => isPlayable && handleGameClick(game.id)}
                >
                  <div className={`h-32 bg-gradient-to-br ${game.color} flex items-center justify-center relative overflow-hidden`}>
                    <img
                      src={game.image}
                      alt={t(game.nameKey)}
                      className={`w-full h-full object-cover transition-transform duration-300 ${isPlayable ? 'group-hover:scale-110' : 'grayscale'}`}
                    />
                    {/* Overlay para juegos no disponibles */}
                    {!game.available && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="text-casino-gold font-cinzel text-sm">{t('games.comingSoon')}</span>
                      </div>
                    )}
                    {/* Overlay para juegos que requieren registro */}
                    {isLocked && (
                      <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2">
                        <Lock className="w-8 h-8 text-casino-gold" />
                        <span className="text-casino-gold font-cinzel text-xs text-center px-2">
                          {t('games.requiresAccount')}
                        </span>
                      </div>
                    )}
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {t(game.nameKey)}
                      {isLocked && <Lock className="w-4 h-4 text-casino-gold/60" />}
                    </CardTitle>
                    <CardDescription>
                      {isLocked ? t('games.registerToPlay') : t(game.descKey)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isPlayable ? (
                      <Button variant="gold" className="w-full" size="sm">
                        {t('games.play')}
                      </Button>
                    ) : isLocked ? (
                      <Button
                        variant="outline"
                        className="w-full border-casino-gold/50 text-casino-gold/70"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleGoToRegister()
                        }}
                      >
                        <Lock className="w-3 h-3 mr-2" />
                        {t('games.createAccount')}
                      </Button>
                    ) : (
                      <Button variant="outline" className="w-full" size="sm" disabled>
                        {t('games.notAvailable')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )
          })}
        </div>

        {/* Quick actions for demo */}
        {isDemo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-12 text-center"
          >
            <p className="text-gray-500 mb-4">
              {t('lobby.likeIt')}
            </p>
            <Button variant="gold" onClick={handleGoToRegister}>
              {t('landing.createAccount').toUpperCase()}
            </Button>
          </motion.div>
        )}
      </div>

      {/* Transaction Modals */}
      <DepositModal
        isOpen={activeModal === 'deposit'}
        onClose={() => setActiveModal(null)}
      />
      <WithdrawModal
        isOpen={activeModal === 'withdraw'}
        onClose={() => setActiveModal(null)}
      />
      <HistoryModal
        isOpen={activeModal === 'history'}
        onClose={() => setActiveModal(null)}
      />
    </>
  )
}
